import { useRef } from "react";
import { useSelector } from "react-redux";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
} from "../../../../../components";
import { MuiDoubleLabelWithChip } from "../../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../../components/statusBadge/StatusBadge";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  keyDetailsSelector,
  projectStatusSelector,
} from "../../../../../redux/slices/project/projectSelector";
import { keyProjectDetailsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE } from "../../../../../helpers/constants";

export const KeyDetail = () => {
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const projectStatus = useSelector(projectStatusSelector) as StatusBadgeType;
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { commonARIALabel } = useNavigationSiteCore();
  const getLabel = (key: string) => {
    return fetchLabel(keyProjectDetailsSiteCoreKeys.keyProjectDetails, key);
  };

  const productLinesRef = useRef<HTMLDivElement>(null);
  const poNumbersRef = useRef<HTMLDivElement>(null);

  const productLineChipsValue = projectKeyDetails?.productLine
    ? setApiDataToDropDownOption(projectKeyDetails.productLine)
    : [];

  const poNumbersChipsValue = projectKeyDetails?.poNumber
    ? setApiDataToDropDownOption(projectKeyDetails.poNumber)
    : [];

  const isClientTypeNCBP = () => {
    return (
      projectKeyDetails?.clientType ===
      CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE
    );
  };

  return (
    <MuiBox>
      <MuiGrid
        container
        data-testid="key-detail-tab"
        className="key-detail-tab"
      >
        {isNotEmpty(projectKeyDetails?.projectKey) && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(keyProjectDetailsSiteCoreKeys.projectKey)}
              value={projectKeyDetails?.projectKey}
              isValueWithEllipsis
            />
          </MuiGrid>
        )}
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(keyProjectDetailsSiteCoreKeys.projectName)}
            value={projectKeyDetails?.projectName}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(keyProjectDetailsSiteCoreKeys.client)}
            value={projectKeyDetails?.client?.name}
            isValueWithEllipsis
          />
          <MuiTypography
            component="h4"
            className="value-style"
            variant="h4Bold"
          >
            {projectKeyDetails?.clientCode}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(keyProjectDetailsSiteCoreKeys.clientBusinessUnit)}
            value={projectKeyDetails?.clientBusinessUnit}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(keyProjectDetailsSiteCoreKeys.clientCostCenter)}
            value={projectKeyDetails?.clientCostCentre}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(
              keyProjectDetailsSiteCoreKeys.primaryDeliveryOffice,
            )}
            value={projectKeyDetails?.primaryDeliveryOffice?.name}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(
              keyProjectDetailsSiteCoreKeys.primaryDeliveryCountry,
            )}
            value={projectKeyDetails?.primaryDeliveryCountry?.name}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabelWithChip
            label={getLabel(keyProjectDetailsSiteCoreKeys.productLineDropDown)}
            replaceEmptyWithDash={!productLineChipsValue.length}
            value={productLineChipsValue}
            chipPopoverGridRef={productLinesRef}
            chipPopoverHeaderLabel={getLabel(
              keyProjectDetailsSiteCoreKeys.productLineDropDown,
            )}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
            countToWrap={1}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(
              keyProjectDetailsSiteCoreKeys.primaryProductLineDropDown,
            )}
            value={projectKeyDetails?.primaryProductLine?.name}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabelWithChip
            label={getLabel(keyProjectDetailsSiteCoreKeys.poNumberDropdown)}
            replaceEmptyWithDash={!poNumbersChipsValue.length}
            value={poNumbersChipsValue}
            chipPopoverGridRef={poNumbersRef}
            chipPopoverHeaderLabel={getLabel(
              keyProjectDetailsSiteCoreKeys.poNumberDropdown,
            )}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(
              keyProjectDetailsSiteCoreKeys.projectFrameworkDropDown,
            )}
            value={projectKeyDetails?.projectFramework?.name}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(keyProjectDetailsSiteCoreKeys.salesOffice)}
            value={projectKeyDetails?.salesOffice?.name}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(keyProjectDetailsSiteCoreKeys.billingOffice)}
            value={projectKeyDetails?.billingOffice?.name}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(keyProjectDetailsSiteCoreKeys.projectStatus)}
            value={""}
          />
          <StatusBadge
            status={projectStatus}
            label={fetchValueByKeyOfDropdown(
              keyProjectDetailsSiteCoreKeys.keyProjectDetails,
              keyProjectDetailsSiteCoreKeys.projectStatusList,
              projectStatus,
            )}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(keyProjectDetailsSiteCoreKeys.dossierNumber)}
            value={projectKeyDetails?.dossierNumber}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>
        {isClientTypeNCBP() && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(keyProjectDetailsSiteCoreKeys.actualClient)}
              value={projectKeyDetails?.actualClient?.name}
              isValueWithEllipsis
              replaceEmptyWithDash
            />
            <MuiTypography
              component="h4"
              className="value-style"
              variant="h4Bold"
            >
              {projectKeyDetails?.actualClientCode}
            </MuiTypography>
          </MuiGrid>
        )}
      </MuiGrid>
    </MuiBox>
  );
};
